import { createTheme, type ThemeOptions } from '@mui/material/styles';
import { colors, fonts, fontWeights, radius, spacing } from './tokens';

const themeOptions: ThemeOptions = {
  palette: {
    mode: 'light',
    primary: {
      main: colors.accent,
      light: colors.accentHover,
      dark: colors.accentActive,
      contrastText: colors.white,
    },
    secondary: {
      main: colors.textMuted,
      contrastText: colors.textPrimary,
    },
    background: {
      default: colors.bg,
      paper: colors.surface,
    },
    text: {
      primary: colors.textPrimary,
      secondary: colors.textSecondary,
    },
    success: {
      main: colors.success,
    },
    warning: {
      main: colors.warning,
    },
    error: {
      main: colors.danger,
    },
    divider: colors.border,
  },
  typography: {
    fontFamily: fonts.body,
    h1: {
      fontFamily: fonts.heading,
      fontWeight: fontWeights.semibold,
      fontSize: '32px',
      lineHeight: '40px',
    },
    h2: {
      fontFamily: fonts.heading,
      fontWeight: fontWeights.semibold,
      fontSize: '24px',
      lineHeight: '32px',
    },
    h3: {
      fontFamily: fonts.body,
      fontWeight: fontWeights.semibold,
      fontSize: '18px',
      lineHeight: '26px',
    },
    body1: {
      fontFamily: fonts.body,
      fontWeight: fontWeights.regular,
      fontSize: '14px',
      lineHeight: '22px',
    },
    body2: {
      fontFamily: fonts.body,
      fontWeight: fontWeights.regular,
      fontSize: '12px',
      lineHeight: '16px',
    },
    button: {
      fontFamily: fonts.body,
      fontWeight: fontWeights.semibold,
      fontSize: '14px',
      textTransform: 'none',
    },
    caption: {
      fontFamily: fonts.body,
      fontWeight: fontWeights.semibold,
      fontSize: '12px',
      lineHeight: '16px',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    },
  },
  shape: {
    borderRadius: 14, // md default
  },
  spacing: 8, // Base unit
  shadows: [
    'none',
    '0 1px 2px rgba(31, 41, 51, 0.04)',
    '0 2px 4px rgba(31, 41, 51, 0.06)',
    '0 4px 8px rgba(31, 41, 51, 0.08)',
    '0 8px 16px rgba(31, 41, 51, 0.10)',
    '0 12px 24px rgba(31, 41, 51, 0.12)',
    '0 16px 32px rgba(31, 41, 51, 0.14)',
    '0 20px 40px rgba(31, 41, 51, 0.16)',
    ...Array(17).fill('none'),
  ] as import('@mui/material/styles').Shadows,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: colors.bg,
          color: colors.textPrimary,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          padding: `${spacing[3]} ${spacing[6]}`,
          fontWeight: fontWeights.semibold,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          backgroundColor: colors.brass,
          color: colors.textPrimary,
          '&:hover': {
            backgroundColor: colors.brassHover,
          },
          '&:active': {
            backgroundColor: colors.brassHover,
          },
          '&:disabled': {
            backgroundColor: colors.border,
            color: colors.textMuted,
          },
        },
        outlined: {
          borderColor: colors.border,
          color: colors.textPrimary,
          '&:hover': {
            borderColor: colors.accent,
            backgroundColor: 'transparent',
          },
        },
        text: {
          color: colors.textPrimary,
          '&:hover': {
            backgroundColor: colors.surfaceRaised,
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: radius.sm,
          color: colors.textMuted,
          '&:hover': {
            backgroundColor: colors.surfaceRaised,
            color: colors.textPrimary,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: radius.md,
            backgroundColor: colors.surface,
            '& fieldset': {
              borderColor: colors.border,
            },
            '&:hover fieldset': {
              borderColor: colors.textMuted,
            },
            '&.Mui-focused fieldset': {
              borderColor: colors.accent,
              borderWidth: '1px',
            },
          },
          '& .MuiInputLabel-root': {
            color: colors.textMuted,
            '&.Mui-focused': {
              color: colors.accent,
            },
          },
          '& .MuiInputBase-input': {
            color: colors.textPrimary,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: radius.md,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: colors.surface,
          backgroundImage: 'none',
          borderRadius: radius.lg,
        },
        elevation1: {
          boxShadow: 'none',
          border: `1px solid ${colors.border}`,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: colors.surface,
          borderRadius: radius.lg,
          border: `1px solid ${colors.border}`,
          boxShadow: 'none',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.surfaceRaised,
          borderRadius: radius.xl,
          border: `1px solid ${colors.border}`,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.surfaceRaised,
          border: `1px solid ${colors.border}`,
          borderRadius: radius.md,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: colors.surface,
          },
          '&.Mui-selected': {
            backgroundColor: colors.surface,
            '&:hover': {
              backgroundColor: colors.surface,
            },
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 'auto',
        },
        indicator: {
          backgroundColor: colors.accent,
          height: '2px',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: fontWeights.medium,
          color: colors.textMuted,
          minHeight: 'auto',
          padding: `${spacing[3]} ${spacing[4]}`,
          '&.Mui-selected': {
            color: colors.textPrimary,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: radius.sm,
          fontWeight: fontWeights.medium,
        },
        filled: {
          backgroundColor: colors.surfaceRaised,
        },
        outlined: {
          borderColor: colors.border,
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: colors.surfaceRaised,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.textPrimary,
          color: colors.bg,
          borderRadius: radius.sm,
          fontSize: '12px',
          fontWeight: fontWeights.medium,
          padding: `${spacing[1]} ${spacing[3]}`,
        },
        arrow: {
          color: colors.textPrimary,
        },
      },
    },
  },
};

export { themeOptions };
export const theme = createTheme(themeOptions);
